import React from 'react';
import {
  Box,
  Typography,
  Chip,
  CircularProgress,
  Stack,
  Alert,
  Divider,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { EmaQuestionnaire } from '@aphp-ema/common/questionnaire';
import { useGetQuestionnaireScheduling } from '@/features/scheduling/api';
import NotificationsIcon from '@mui/icons-material/Notifications';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { DayNameOrEveryDay } from '@aphp-ema/common/scheduling/dayNames';
import { ClockIcon } from '@mui/x-date-pickers';
import { EmaQuestionnaireSchedulingConfiguration } from './EmaQuestionnaireSchedulingConfiguration';

type EmaQuestionnaireNotificationSchedulingProps = {
  emaQuestionnaire: EmaQuestionnaire;
};

export const EmaQuestionnaireNotificationScheduling: React.FC<
  EmaQuestionnaireNotificationSchedulingProps
> = ({ emaQuestionnaire }) => {
  const { t } = useTranslation();
  const {
    data: schedulingData,
    isLoading,
    error,
  } = useGetQuestionnaireScheduling(emaQuestionnaire.id);

  const formatTimeString = (timeString: string): string => {
    // timeString is in format 'hh:mm:ss', we want to display 'hh:mm'
    const [hours, minutes] = timeString.split(':');
    return `${hours}:${minutes}`;
  };

  const getDayTranslation = (day: DayNameOrEveryDay): string => {
    return {
      MONDAY: t('days.monday'),
      TUESDAY: t('days.tuesday'),
      WEDNESDAY: t('days.wednesday'),
      THURSDAY: t('days.thursday'),
      FRIDAY: t('days.friday'),
      SATURDAY: t('days.saturday'),
      SUNDAY: t('days.sunday'),
      EVERY_DAY: t('days.EVERY_DAY'),
    }[day];
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          py: 2,
          px: 3,
          borderRadius: 1.5,
          border: '1px solid',
          borderColor: 'grey.200',
        }}
      >
        <CircularProgress size={18} sx={{ mr: 1.5, color: 'primary.main' }} />
        <Typography variant="body2" color="text.secondary" fontWeight="500">
          {t(
            'pages.studies.emaQuestionnaire.scheduling.loading',
            'Loading scheduling...',
          )}
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert
        severity="error"
        icon={<ErrorOutlineIcon />}
        sx={{
          borderRadius: 1.5,
          '& .MuiAlert-message': {
            fontWeight: 500,
          },
        }}
      >
        {t(
          'pages.studies.emaQuestionnaire.scheduling.error',
          'Error loading scheduling',
        )}
      </Alert>
    );
  }

  if (
    !schedulingData ||
    (schedulingData.schedulings.length === 0 && !schedulingData.configuration)
  ) {
    return (
      <Alert
        severity="info"
        icon={<NotificationsIcon />}
        sx={{
          borderRadius: 1.5,
          '& .MuiAlert-message': {
            fontWeight: 500,
          },
        }}
      >
        {t('pages.studies.emaQuestionnaire.scheduling.noScheduling')}
      </Alert>
    );
  }

  const hasSchedulings = schedulingData.schedulings.length > 0;
  const hasConfiguration = !!schedulingData.configuration;

  return (
    <Box>
      {hasSchedulings && (
        <Stack
          direction="row"
          flexWrap="wrap"
          gap={1}
          mb={hasConfiguration ? 2 : 0}
        >
          {schedulingData.schedulings.map((scheduling) => (
            <Chip
              key={scheduling.id}
              icon={<ClockIcon sx={{ fontSize: 16 }} />}
              label={
                getDayTranslation(scheduling.day) +
                ` : ${formatTimeString(scheduling.startTime)}${
                  scheduling.endTime &&
                  scheduling.startTime !== scheduling.endTime
                    ? ` - ${formatTimeString(scheduling.endTime)}`
                    : ''
                }`
              }
              variant="outlined"
              size="small"
              color="success"
              sx={{
                fontWeight: 500,
                borderRadius: 1.5,
              }}
            />
          ))}
        </Stack>
      )}

      {!hasSchedulings && hasConfiguration && (
        <Alert
          severity="info"
          icon={<NotificationsIcon />}
          sx={{
            borderRadius: 1.5,
            mb: 2,
            '& .MuiAlert-message': {
              fontWeight: 500,
            },
          }}
        >
          {t('pages.studies.emaQuestionnaire.scheduling.noScheduling')}
        </Alert>
      )}

      {hasConfiguration && schedulingData.configuration && (
        <>
          {hasSchedulings && <Divider sx={{ my: 2 }} />}
          <EmaQuestionnaireSchedulingConfiguration
            configuration={schedulingData.configuration}
          />
        </>
      )}
    </Box>
  );
};
